import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, AlertCircle, Search, RefreshCcw, X, Clock, ExternalLink, ChevronRight, ChevronDown } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import logIntelligenceService from './services/logIntelligenceService';
// Reutilises tels quels depuis ReadyApi.js : meme service de logs, meme
// authentification Elasticsearch par zone, meme route serveur
// (POST /api/logs/search). Ecrire une seconde implementation ici aurait
// donne deux comportements a maintenir pour une seule question.
import elasticAuthService from './services/elasticAuthService';
import { ZONES_TRACE } from './services/traceZone';
import ElasticAuthModal from './ElasticAuthModal';

// Org/projet confirmes par `git remote -v` de ce monorepo
// (dev.azure.com/chapsvision/Fabrique) - lien direct vers un Work Item existant,
// jamais de recherche/creation via l'API ADO (decision explicite avec l'utilisateur).
const WORK_ITEM_BASE_URL = 'https://dev.azure.com/chapsvision/Fabrique/_workitems/edit/';
const NO_TEAM = 'Non assignée';

// "new" veut dire "jamais triee", pas "vient d'apparaitre" - rien ne fait
// evoluer ce statut automatiquement (ca viendra avec les actions de triage,
// hors perimetre de cette page en lecture seule). Le concept "vient d'etre
// vu pour la premiere fois LORS DE CE RUN" existe (detect.js, kind: 'new')
// mais n'est pas stocke sur la signature - ne pas le confondre avec ce statut.
const STATUS_LABELS = {
  new: 'Non triée',
  triaged: 'Triée',
  known_issue: 'Connue',
  muted: 'Muette',
  fixed: 'Corrigée',
  wont_fix: "Ne sera pas corrigée"
};

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    new: 'bg-blue-100 text-blue-700',
    triaged: 'bg-purple-100 text-purple-700',
    known_issue: 'bg-gray-100 text-gray-700',
    muted: 'bg-gray-100 text-gray-500',
    fixed: 'bg-green-100 text-green-700',
    wont_fix: 'bg-gray-100 text-gray-500'
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// Couleurs reprises du mock section 12 du cadrage (--sev-blocker/major/minor/noise).
const SEVERITY_LABELS = { blocker: 'Bloquant', major: 'Majeur', minor: 'Mineur', noise: 'Bruit' };
const SEVERITY_STYLES = {
  blocker: 'bg-red-100 text-red-700',
  major: 'bg-orange-100 text-orange-700',
  minor: 'bg-blue-100 text-blue-700',
  noise: 'bg-gray-100 text-gray-500'
};

const URL_PR = 'https://dev.azure.com/chapsvision/Fabrique/_git';

// Au-dela de ce nombre, la liste des PR livrees n'aide plus : elle ne designe
// personne et coute au lecteur le temps de la parcourir. Mesure le 15/09/2026 :
// une fenetre de 18 h avec UN SEUL deploiement contenait 57 PR - un train de
// release embarque une semaine de merges accumules.
const SEUIL_ENUMERATION = 10;

// L'heure à laquelle l'erreur s'est produite, et non celle du run qui l'a
// découverte. Relevé en production le 15/09/2026 : 489 signatures portaient
// 06:00 — l'heure de la collecte — et 251 sur 312 étaient datées du lendemain
// de leur première occurrence. Impossible, dans ces conditions, de remettre
// deux erreurs du même jour dans l'ordre.
//
// `first_ts` n'existe que pour ce qui a été collecté depuis cette date ; avant,
// on ne connaît que le jour, et on le dit plutôt que d'afficher une heure
// inventée.
function quandEstCeArrive(reel, dateDuRun) {
  if (reel) {
    return { texte: `${reel.slice(8, 10)}/${reel.slice(5, 7)} ${reel.slice(11, 16)}`, exact: true, brut: reel };
  }
  const jour = (dateDuRun || '').slice(0, 10);
  return { texte: jour ? `${jour.slice(8, 10)}/${jour.slice(5, 7)}` : '—', exact: false, brut: dateDuRun };
}

// `quoi` nomme ce que la date raconte : la même cellule sert à « Détecté
// depuis » et à « Dernière occurrence », et une infobulle qui dit « Première
// erreur » sous la seconde colonne serait un contresens.
function Horodatage({ reel, dateDuRun, quoi = 'Première erreur' }) {
  const q = quandEstCeArrive(reel, dateDuRun);
  return (
    <span
      className={q.exact ? '' : 'text-gray-400'}
      title={q.exact
        ? `${quoi} le ${q.brut}`
        // Formulé sans dire de quelle date il s'agit : la même phrase sert aux
        // deux colonnes. Elle parlait de la signature « cataloguée avant que
        // l'outil enregistre l'heure », ce qui décrit la première détection —
        // sous « Dernière occurrence » elle affirmait quelque chose que la
        // donnée ne dit pas. Le JOUR, lui, est exact dans les deux cas : il
        // vient de occurrence.day, jamais d'une date de run.
        : "Heure inconnue : seul le jour est connu, et il est exact. L'outil n'enregistre l'heure réelle des erreurs que depuis le 15/09/2026."}
    >
      {q.texte}
    </span>
  );
}

// Le ticket de bug lie, sous le statut.
//
// PAS une colonne a part : mesure du 09/10/2026, 1 signature sur 1246 en
// porte un (1 probleme sur 586). Une dixieme colonne aurait pris ~6 % de
// large a "Message" et "Type d'exception" - qui tronquent deja - pour rester
// vide sur 585 lignes sur 586.
//
// Sous le statut, en revanche, elle ne coute rien quand il n'y a pas de
// ticket, et elle est a l'endroit logique : statut et ticket sont le meme
// geste de triage.
function TicketLie({ partage }) {
  const ids = partage?.valeurs || [];
  if (ids.length === 0) return null;

  // "1/6" quand le ticket ne couvre pas tout le groupe. Sans ce repere, le
  // parent annoncerait un probleme suivi alors que cinq variantes sur six ne
  // le sont pas - c'est l'etat reel du catalogue, pas un cas theorique.
  const partiel = partage.total > 1 && partage.portees < partage.total;
  const couverture = partiel ? ` ${partage.portees}/${partage.total}` : '';
  const infobulle = partiel
    ? `${partage.portees} variante(s) sur ${partage.total} portent ce ticket`
    : `Ouvrir le ticket ${ids.join(', ')}`;

  return (
    <span className="block text-[10px] mt-0.5 truncate" title={infobulle}>
      {ids.map((id, i) => (
        <React.Fragment key={id}>
          {i > 0 && <span className="text-gray-400">, </span>}
          <a
            href={`${WORK_ITEM_BASE_URL}${encodeURIComponent(id)}`}
            target="_blank" rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-blue-600 hover:underline"
          >
            #{id}
          </a>
        </React.Fragment>
      ))}
      {couverture && <span className="text-amber-600 font-medium">{couverture}</span>}
    </span>
  );
}

// L'equipe assignee, meme traitement que le ticket : on NOMME les equipes
// quand elles different, au lieu d'ecrire "plusieurs" - qui obligeait a
// deplier pour une information qui tient dans la cellule. Et on dit quand
// l'assignation ne couvre qu'une partie des variantes.
//
// Le cas deviendra frequent : des qu'une NOUVELLE variante rejoint un
// probleme deja trie, elle arrive sans equipe ni ticket. C'est precisement ce
// qu'un developpeur doit voir, pas ce qu'il faut cacher derriere un mot vague.
function EquipeAssignee({ partage, vide }) {
  const noms = partage?.valeurs || [];
  if (noms.length === 0) return <span className="text-gray-400">{vide}</span>;

  const partiel = partage.total > 1 && partage.portees < partage.total;
  return (
    <span
      className="truncate"
      title={partiel
        ? `${noms.join(', ')} — ${partage.portees} variante(s) sur ${partage.total} assignee(s)`
        : noms.join(', ')}
    >
      {noms.join(', ')}
      {partiel && <span className="text-amber-600 font-medium"> {partage.portees}/{partage.total}</span>}
    </span>
  );
}

// Un lien vers la PR, identique partout : le clic ne doit jamais déplier ou
// replier la ligne sous lui.
function LienPr({ pr, gras }) {
  return (
    <a
      href={`${URL_PR}/${pr.repo || 'argonos'}/pullrequest/${pr.pr_id}`}
      target="_blank" rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`${gras ? 'font-medium ' : ''}text-blue-600 hover:underline`}
      title={pr.title || ''}
    >
      PR {pr.pr_id}
    </a>
  );
}

// [[depot, [pr...]], ...] dans l'ordre de première apparition : l'ordre rendu
// par la corrélation est conservé à l'intérieur de chaque dépôt.
function grouperParDepot(prs) {
  const groupes = new Map();
  for (const pr of prs) {
    const depot = pr.repo || 'argonos';
    if (!groupes.has(depot)) groupes.set(depot, []);
    groupes.get(depot).push(pr);
  }
  return [...groupes.entries()];
}

// Le sens inverse du lien : cette campagne a tourné, quelles erreurs sont
// tombées pendant ?
//
// C'est la question du matin devant un rapport de test rouge, et elle se règle
// aujourd'hui à la main dans Kibana. Une campagne sans erreur rattachée est
// affichée quand même : « rien n'est tombé pendant » est une réponse.
function LigneCampagne({ campagne, ouverte, onToggle, signatures, chargement, onSelectSignature }) {
  const r = RESULTAT_CAMPAGNE[campagne.result] || { texte: campagne.result || '—', classe: 'text-gray-400' };
  const minutes = Math.round((new Date(campagne.finished_at) - new Date(campagne.started_at)) / 60000);

  return (
    <>
      <tr
        onClick={() => onToggle(campagne.id)}
        className="border-t border-gray-100 hover:bg-blue-50/50 cursor-pointer"
      >
        <td className="px-4 py-2 text-gray-400">
          {ouverte ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-4 py-2">
          <span className="font-medium text-gray-800">{campagne.suite}</span>
          <div className="text-xs text-gray-500">{campagne.pipeline}</div>
        </td>
        <td className="px-4 py-2 text-gray-600">{campagne.version || '—'}</td>
        <td className="px-4 py-2 text-gray-600">{campagne.env}</td>
        <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
          {String(campagne.started_at).slice(8, 10)}/{String(campagne.started_at).slice(5, 7)}{' '}
          {String(campagne.started_at).slice(11, 16)}
          <span className="text-gray-400"> · {minutes} min</span>
        </td>
        <td className={`px-4 py-2 whitespace-nowrap ${r.classe}`}>{r.texte}</td>
        <td className="px-4 py-2 text-right tabular-nums">
          {/* Zéro n'est pas un vide : c'est « aucune erreur nouvelle pendant
              cette campagne », ce qui est une bonne nouvelle. */}
          {campagne.signatures > 0
            ? <span className="font-medium text-gray-800">{campagne.signatures}</span>
            : <span className="text-gray-300">0</span>}
        </td>
      </tr>

      {ouverte && (
        <tr className="bg-gray-50/60">
          <td />
          <td colSpan={6} className="px-4 py-2">
            {chargement ? (
              <span className="text-xs text-gray-400">Chargement des erreurs…</span>
            ) : !signatures || signatures.length === 0 ? (
              <span className="text-xs text-gray-500">
                Aucune erreur n'est apparue pendant cette exécution.
              </span>
            ) : (
              <ul className="text-xs space-y-1">
                {signatures.map((s) => (
                  <li
                    key={s.id}
                    onClick={(e) => { e.stopPropagation(); onSelectSignature(s.id); }}
                    className="cursor-pointer hover:text-blue-700"
                  >
                    <span className="text-gray-400">{String(s.first_ts).slice(11, 19)}</span>
                    {' · '}
                    <span className="text-gray-500">{s.service}</span>
                    {' · '}
                    <span className="text-gray-800">{s.ai_title || s.exception_type}</span>
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// Les campagnes de test pendant lesquelles l'erreur est apparue.
//
// LE VOCABULAIRE EST TOUT : « apparue pendant », jamais « causée par ».
// Mesuré le 15/09/2026 sur 36 h réelles, une signature apparaît pendant une
// campagne 2,1 fois plus souvent que le hasard sur qa3 et 2,5 fois sur qa4 —
// un signal réel, mesuré deux fois indépendamment, mais qui ne désigne
// personne. Quatre des six erreurs de qa4 sont d'ailleurs apparues pendant des
// campagnes au vert.
//
// Ce que ça donne au lecteur, et c'est déjà beaucoup : quel test rejouer, sur
// quelle branche, et à quelle équipe en parler.
const RESULTAT_CAMPAGNE = {
  succeeded: { texte: 'au vert', classe: 'text-emerald-600' },
  succeededWithIssues: { texte: 'avec des alertes', classe: 'text-amber-600' },
  failed: { texte: 'en échec', classe: 'text-red-600' },
  canceled: { texte: 'annulée', classe: 'text-gray-400' }
};

function CampagnesDeLaSignature({ campaigns, env }) {
  if (!campaigns || campaigns.length === 0) {
    // Le silence dit desormais SUR QUOI il porte : la liste est bornee au meme
    // environnement que le reste de la fiche, et "aucune campagne" sans cette
    // precision laisserait croire qu'il n'y en a eu nulle part.
    return (
      <div className="text-sm text-gray-400">
        Aucune campagne de test ne tournait{env ? ` sur ${env}` : ''} — ou l'heure de cette
        erreur n'est pas connue.
      </div>
    );
  }

  return (
    <ul className="text-sm space-y-1.5">
      {campaigns.map((c) => {
        const r = RESULTAT_CAMPAGNE[c.result] || { texte: c.result || '—', classe: 'text-gray-400' };
        return (
          <li key={c.id}>
            <span className="font-medium text-gray-800">{c.suite}</span>
            <span className="text-gray-500"> · {c.pipeline}</span>
            {c.version && <span className="text-gray-500"> · {c.version}</span>}
            <div className="text-xs text-gray-500">
              {/* L'heure de l'erreur dans la campagne, pas seulement la
                  campagne : c'est ce qui permet de retrouver le test. */}
              erreur à {String(c.first_ts).slice(11, 16)}, pendant une exécution de{' '}
              {String(c.started_at).slice(11, 16)} à {String(c.finished_at).slice(11, 16)}
              {' — campagne '}<span className={r.classe}>{r.texte}</span>
            </div>
          </li>
        );
      })}
      {/* Sans cette phrase, la section se lirait comme une accusation. */}
      <li className="text-xs text-gray-400 pt-1">
        L'erreur est apparue pendant ces exécutions. C'est un repère temporel, pas une cause.
      </li>
    </ul>
  );
}

// Les pull requests d'UNE signature, dans le panneau de détail.
//
// Le panneau listait « #31402 — titre (confiance medium) » : pas de lien, donc
// un copier-coller pour ouvrir la PR ; pas de raison, donc rien à vérifier ; et
// un message d'absence qui annonçait « Corrélation PR pas encore implémentée
// (Phase 4 du cadrage) » sur des signatures bel et bien corrélées, dont la
// fenêtre ne contenait simplement aucune livraison.
function PrsDeLaSignature({ detail }) {
  const prs = detail.suspectPrs || [];
  const correlation = detail.correlation;

  if (prs.length === 0) {
    // Les trois silences ne veulent pas dire la même chose, et le panneau
    // doit les distinguer comme le fait la ligne du tableau.
    if (!correlation) {
      return (
        <div className="text-sm text-gray-400">
          Pas de corrélation calculée — elle ne l'est qu'à la découverte d'une erreur nouvelle.
        </div>
      );
    }
    if (!correlation.deployments) {
      return (
        <div className="text-sm text-gray-600">
          Aucune livraison entre la dernière collecte sans cette erreur et son apparition — ce n'est pas une régression de code.
        </div>
      );
    }
    return (
      <div className="text-sm text-gray-500">
        Des livraisons ont eu lieu dans la fenêtre, mais aucune pull request n'a pu en être tirée.
        {correlation.warnings?.[0] && <span className="text-gray-400"> {correlation.warnings[0]}</span>}
      </div>
    );
  }

  // Déjà triées par rang : le fichier d'où l'erreur est levée d'abord, puis
  // les zones, puis le reste de ce que le déploiement a embarqué.
  return (
    <ul className="text-sm space-y-1">
      {prs.map((pr) => (
        <li key={pr.pr_id} className={pr.recoupement ? 'text-gray-800' : 'text-gray-600'}>
          <LienPr pr={pr} gras={Boolean(pr.recoupement)} />
          {pr.title && <span> — {pr.title}</span>}
          {/* La première phrase de la raison nomme le fichier ou la zone ; la
              seconde redit le déploiement, déjà affiché plus haut. */}
          {pr.recoupement && (
            <div className="text-xs text-gray-500 pl-1">{(pr.rationale || '').split('. ')[0]}</div>
          )}
        </li>
      ))}
    </ul>
  );
}

// Ce que le déploiement précédant l'apparition du problème a livré.
//
// Le mot compte : « livrées par le déploiement », jamais « à l'origine de ».
// Mesuré en prod le 14/09/2026, sept erreurs Cassandra se sont vu attribuer une
// PR sur les entités KB — uniquement parce qu'elle était la seule de
// l'intervalle. Le composant montre ce qui a été livré ; c'est au développeur
// de juger le lien.
//
// Trois états distincts, et aucun n'est un vide muet : pas encore corrélé,
// corrélé sans résultat (avec les raisons), et le résultat lui-même.
function DeploymentCorrelation({ state }) {
  if (!state) return <span className="text-xs text-gray-400">Recherche du déploiement associé…</span>;
  if (state.error) return <span className="text-xs text-red-600">Corrélation indisponible : {state.error}</span>;

  if (!state.correlated) {
    return (
      <span className="text-xs text-gray-400">
        Pas de corrélation calculée — elle ne l'est qu'à la découverte d'une erreur nouvelle.
      </span>
    );
  }

  const jour = state.deploymentDate ? state.deploymentDate.slice(0, 10) : null;
  const heure = state.deploymentDate ? state.deploymentDate.slice(11, 16) : null;
  const inchanges = (state.repos || []).filter((r) => r.inchange).map((r) => r.repo);

  // Trois forces de recoupement, et l'écran ne doit surtout pas les confondre.
  //
  // « fichier » : la PR modifie le fichier même d'où l'erreur est levée — le
  // chemin est écrit dans la pile d'appel. C'est une désignation, vérifiable
  // en un clic.
  // « repertoire » / « namespace » : la PR a touché la zone, pas le code. Des
  // dizaines de fichiers pour un répertoire, des centaines pour un projet.
  // C'est un filtre utile (médiane de 2 PR retenues sur 25 livrées, relevé le
  // 15/09/2026) mais ce n'est pas une désignation, et 137 des 157 recoupements
  // en prod sont de ce type. Les présenter pareil ferait passer une zone pour
  // un coupable.
  const parFichier = (state.pullRequests || []).filter((pr) => pr.recoupement === 'fichier');
  const parZone = (state.pullRequests || []).filter((pr) => ['repertoire', 'namespace'].includes(pr.recoupement));
  // La zone est la même pour toutes : celle de la pile d'appel. La raison la
  // nomme, entre « touche » et la virgule suivante.
  // Null pour les corrélations écrites AVANT ce changement : leur raison porte
  // l'ancienne formulation, que cette expression ne reconnaît pas. La phrase
  // s'en passe alors plutôt que d'afficher un nom inventé — et le rattrapage
  // (`node bin/correlate.js --force --apply`) les réécrit.
  const zoneConcernee = (parZone[0]?.rationale || '').match(/[Tt]ouche ([\w./-]+)/)?.[1] || null;
  const natureDeLaZone = parZone[0]?.recoupement === 'repertoire' ? 'le répertoire' : 'le projet';

  return (
    <div className="text-xs space-y-1">
      {/* La fenêtre d'abord : « 12 PR » ne veut pas dire la même chose selon
          qu'elles ont été livrées en quatre heures ou en sept jours. C'est ce
          qui distingue une piste d'une liste, et le lecteur doit pouvoir en
          juger avant de regarder les PR elles-mêmes. */}
      <div className="text-gray-600">
        <span className="font-medium text-gray-700">Entre la dernière collecte sans cette erreur et son apparition</span>
        {state.windowFrom && (
          <span className="text-gray-800"> : {state.windowFrom.slice(8, 10)}/{state.windowFrom.slice(5, 7)} {state.windowFrom.slice(11, 16)}
            {' → '}{state.windowTo?.slice(8, 10)}/{state.windowTo?.slice(5, 7)} {state.windowTo?.slice(11, 16)}</span>
        )}
        {state.deployments > 0 && (
          <span className="text-gray-500"> · {state.deployments} déploiement{state.deployments > 1 ? 's' : ''}</span>
        )}
      </div>
      {jour && (
        <div className="text-gray-500">
          Dernier déploiement de la fenêtre : {jour} à {heure}
          {state.servicesChanged > 0 && <> · {state.servicesChanged} composant(s) modifié(s)</>}
        </div>
      )}

      {/* Aucune livraison dans la fenêtre : c'est une RÉPONSE, la plus utile de
          toutes — inutile de chercher un coupable côté code. Mesuré le
          15/09/2026, c'est le cas de 125 signatures sur 598, et elles
          s'affichaient jusqu'ici comme un silence. */}
      {state.deployments === 0 ? (
        <div className="text-gray-600">
          Aucune livraison dans cette fenêtre — ce n'est pas une régression de code.
        </div>
      ) : parFichier.length > 0 ? (
        /* Le seul cas où une PR est DÉSIGNÉE : elle modifie le fichier même
           d'où l'erreur est levée — la pile d'appel en donne le chemin exact.
           Mesuré le 15/09/2026, c'est rare, et c'est précisément pour ça que
           ça mérite la première ligne. */
        <div className="space-y-0.5">
          {parFichier.slice(0, 3).map((pr) => (
            <div key={pr.pr_id}>
              <LienPr pr={pr} gras />
              {/* La première phrase de la raison nomme le fichier. Le
                  développeur la vérifie lui-même en ouvrant la PR ; c'est
                  précisément ce qu'un score ne permet pas. */}
              <span className="text-gray-600"> — {(pr.rationale || '').split('. ')[0]}</span>
            </div>
          ))}
          <div className="text-gray-400">
            Parmi {state.pullRequests.length} PR livrées dans cette fenêtre.
          </div>
        </div>
      ) : parZone.length > 0 ? (
        /* Beaucoup plus faible, et le texte doit le dire : un répertoire porte
           des dizaines de fichiers, un projet .NET des centaines. « A touché la
           même zone » est un FILTRE, pas une désignation. Il reste utile —
           médiane de 2 PR retenues sur 25 livrées — à condition d'annoncer ce
           qu'il est. */
        <div className="space-y-0.5">
          <div className="text-gray-600">
            {parZone.length} PR sur {state.pullRequests.length} touchent{' '}
            {zoneConcernee && <><span className="font-medium text-gray-700">{zoneConcernee}</span>, </>}
            {natureDeLaZone} d'où l'erreur est levée :
          </div>
          <div>
            {parZone.slice(0, 6).map((pr, i) => (
              <span key={pr.pr_id}>{i > 0 && ', '}<LienPr pr={pr} /></span>
            ))}
            {parZone.length > 6 && <span className="text-gray-500"> +{parZone.length - 6} autres</span>}
          </div>
          <div className="text-gray-400">
            Aucune ne modifie le fichier lui-même — c'est la zone qui correspond, pas le code.
          </div>
        </div>
      ) : state.pullRequests.length > SEUIL_ENUMERATION ? (
        /* Au-delà de dix, énumérer ne rend pas service : une liste de
           cinquante PR ne désigne personne et coûte au lecteur le temps de la
           parcourir. On dit ce qu'on sait — combien — et ce qu'on a cherché. */
        <div className="text-gray-600">
          {state.pullRequests.length} PR livrées dans cette fenêtre.
          {state.recoupementCherche
            ? <span className="text-gray-400"> Aucune ne modifie le code présent dans la pile d'appel.</span>
            : <span className="text-gray-400"> Trop nombreuses pour désigner une piste, et la pile d'appel ne permet pas de trancher.</span>}
        </div>
      ) : state.pullRequests.length > 0 ? (
        <div>
          {/* Deux formulations, parce que les deux choses ne se valent pas.
              « Livrées par » quand l'intervalle entre les deux révisions est
              exact ; « passées sur la lignée » quand les deux builds n'étaient
              pas sur la même branche et qu'on a dû borner par les dates. Sur la
              prod du 14/09/2026, ce second cas représente la majorité — le
              masquer donnerait à une approximation l'aplomb d'une mesure. */}
          <span className="text-gray-600">
            {state.approximate
              ? 'Passées sur la lignée déployée entre les deux versions '
              : 'Livrées par ce déploiement '}
            {state.approximate && (
              <span className="text-gray-400" title="Les deux builds n'étaient pas sur la même lignée : l'intervalle est borné par leurs dates, un commit a pu être écrit avant et fusionné dedans.">
                (approximation)
              </span>
            )}
            {' : '}
          </span>
          {/* Groupées par dépôt, chaque groupe nommé. Un « (shared-devops) »
              accolé à chaque PR hors argonos, et rien aux autres, se lisait
              comme un suffixe portant sur toute la liste : avec deux PR
              shared-devops en fin de ligne, le dépôt semblait cité deux fois et
              argonos jamais. Un seul dépôt, argonos : pas d'étiquette. */}
          {grouperParDepot(state.pullRequests.slice(0, 8)).map(([depot, prs], g, groupes) => (
            <span key={depot}>
              {g > 0 && <span className="text-gray-400"> · </span>}
              {(groupes.length > 1 || depot !== 'argonos') && (
                <span className="text-gray-500">{depot} : </span>
              )}
              {prs.map((pr, i) => (
                <span key={pr.pr_id}>
                  {i > 0 && ', '}
                  <LienPr pr={pr} />
                </span>
              ))}
            </span>
          ))}
          {state.pullRequests.length > 8 && (
            <span className="text-gray-500"> +{state.pullRequests.length - 8} autres</span>
          )}
        </div>
      ) : (
        <div className="text-gray-500">
          Aucune PR identifiée dans l'intervalle
          {state.warnings?.length > 0 && <> — {state.warnings[0]}</>}
        </div>
      )}

      {inchanges.length > 0 && (
        <div className="text-gray-400">
          Inchangé(s) entre les deux versions : {inchanges.join(', ')} — cause écartée.
        </div>
      )}
    </div>
  );
}

function SeverityBadge({ severity }) {
  if (!severity) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_STYLES[severity] || 'bg-gray-100 text-gray-600'}`}>
      {SEVERITY_LABELS[severity] || severity}
    </span>
  );
}

// Remonte par le manager (29/08/2026) : rien ne distinguait visuellement ce
// qui vient de l'IA (titre/severite/categorie, peut halluciner ou paraphraser
// differemment le meme type brut d'un appel a l'autre) de ce qui est brut
// (message_sample, exception_type, tel quel depuis Elasticsearch).
function AiBadge() {
  return (
    <span
      className="inline-flex items-center px-1 py-0.5 text-[10px] leading-none font-semibold text-indigo-600 bg-indigo-50 rounded flex-shrink-0"
      title="Genere par l'IA (titre reformule et severite, quand disponibles) - le modele peut paraphraser ou noter differemment le meme type d'exception brut d'un appel a l'autre."
    >
      IA
    </span>
  );
}

// trace_ids est stocke cote API comme une chaine JSON (echantillon, pas
// exhaustif - cf. schema.sql) ; jamais affiche jusqu'ici cote dashboard.
function parseTraceIds(raw) {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

// La chaine d'exceptions imbriquees. Trois etats a NE PAS confondre, parce
// qu'ils n'appellent pas la meme reaction du lecteur :
//
//   null  -> on n'a jamais regarde. La signature a ete cataloguee avant que la
//            collecte enregistre les niveaux imbriques (07/10/2026). Dire
//            "aucune cause sous-jacente" serait faux.
//   []    -> on a regarde : cette erreur n'en a pas. C'est une reponse.
//   [...] -> la chaine, de la plus proche de la surface a la plus profonde.
//
// Un silence doit dire pourquoi : c'est la meme regle que "aucune livraison
// dans la fenetre", et la meme lecon que first_ts - un champ neuf ne dit rien
// des donnees anciennes.
function CauseSousJacente({ brut }) {
  if (brut === null || brut === undefined) {
    return (
      <div className="text-sm text-gray-400">
        Inconnue : cette erreur a été cataloguée avant que la collecte enregistre les
        exceptions imbriquées. Elle apparaîtra ici dès sa prochaine occurrence.
      </div>
    );
  }

  let chaine;
  try {
    chaine = JSON.parse(brut);
  } catch {
    return <div className="text-sm text-gray-400">Chaîne d'exceptions illisible.</div>;
  }
  if (!Array.isArray(chaine) || chaine.length === 0) {
    return <div className="text-sm text-gray-500">Cette erreur n'a pas d'exception imbriquée.</div>;
  }

  return (
    <div className="bg-gray-50 rounded p-3 space-y-2">
      {chaine.map((niveau, i) => (
        <div key={i} className="flex gap-2 text-sm">
          {/* Le decalage dit la profondeur sans l'ecrire : chaque niveau est
              la cause de celui du dessus. */}
          <span className="text-gray-300 select-none shrink-0" style={{ paddingLeft: `${i * 12}px` }}>↳</span>
          <div className="min-w-0">
            <div className="font-mono text-xs text-gray-700 break-all">{niveau.type}</div>
            {niveau.message && <div className="text-gray-800 break-words">{niveau.message}</div>}
          </div>
        </div>
      ))}
      <div className="text-xs text-gray-400 pt-1">
        Le dernier niveau est la cause la plus profonde rapportée par l'application.
      </div>
    </div>
  );
}

// Les environnements dont le service de logs sait retrouver un TraceId
// (cf. LOGS_ZONES dans server.js). Un TraceId d'ailleurs reste affiche, mais
// en texte simple : proposer un lien qui ne peut pas aboutir serait pire que
// de ne rien proposer.
const ZONES_LOGS = ZONES_TRACE;

// Une ligne de log telle qu'Elasticsearch la rend. On n'affiche que ce qui
// se lit : l'heure, le niveau, le service et le message. Le document complet
// reste accessible en depliant - c'est du JSON, pas une reponse a la question
// qu'on se posait en cliquant.
function LigneDeLog({ hit }) {
  const [ouvert, setOuvert] = useState(false);
  const s = hit?._source || {};
  const heure = String(s['@timestamp'] || '').slice(11, 23);
  const service = s.data_stream?.dataset || s.service?.name || '';
  const niveau = s.level || s.log?.level || '';
  const estErreur = /error|fatal|critical/i.test(niveau);

  // Toute la ligne deplie, pas seulement le chevron. Sauf si le clic termine
  // une selection de texte : copier un message ne doit pas deplier la ligne.
  const basculer = () => {
    if (window.getSelection()?.toString()) return;
    setOuvert((v) => !v);
  };

  return (
    <div className="border-b border-gray-100 py-1.5">
      <div
        role="button"
        tabIndex={0}
        onClick={basculer}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOuvert((v) => !v); } }}
        className="flex items-start gap-2 font-mono text-[11px] px-1 rounded cursor-pointer hover:bg-gray-50"
        title={ouvert ? 'Replier' : 'Voir le document complet'}
      >
        {/* Largeurs : "Information" fait 11 caracteres et ne tenait pas dans
            les 48 px de w-12 - il debordait sur le nom du service, les deux
            se lisaient colles ("Informationargonos_explo"). Chaque colonne a
            desormais la place de son contenu reel, et tronque au besoin. */}
        <span className="text-gray-400 shrink-0 w-[5.5rem]">{heure || '—'}</span>
        <span className={`shrink-0 w-24 truncate ${estErreur ? 'text-red-600 font-semibold' : 'text-gray-500'}`} title={niveau}>
          {niveau || '—'}
        </span>
        <span className="text-gray-500 shrink-0 w-32 truncate" title={service}>{service || '—'}</span>
        <span className="text-gray-800 break-words flex-1 min-w-0">{s.message || '(message vide)'}</span>
        <span className="text-gray-400 shrink-0">
          {ouvert ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </span>
      </div>
      {ouvert && (
        <pre className="mt-1 text-[10px] bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto">
          {JSON.stringify(s, null, 2)}
        </pre>
      )}
    </div>
  );
}

function SignatureDetail({ signatureId, env, probleme, filtres, teams, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [ownerTeam, setOwnerTeam] = useState('');
  const [wiId, setWiId] = useState('');
  const [status, setStatus] = useState('new');
  const [mutedUntil, setMutedUntil] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Remontee des logs applicatifs par TraceId. La zone n'est PAS devinee :
  // chaque occurrence porte son environnement (occ.env), on le passe tel
  // quel. ReadyApi.js, lui, doit la deduire du nom de la suite faute de
  // mieux - ici l'information existe, ce serait absurde de la redeviner.
  const [trace, setTrace] = useState(null); // { id, env }
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceResult, setTraceResult] = useState(null);
  const [traceError, setTraceError] = useState(null);
  const [authZone, setAuthZone] = useState(null);
  const [traceEnAttente, setTraceEnAttente] = useState(null);
  const [traceRefusee, setTraceRefusee] = useState(false);
  // Par defaut on ne montre QUE les lignes d'erreur de la trace. Une trace
  // reelle de qa4 porte 95 580 lignes sur 17 heures pour 94 erreurs : servir
  // "les logs de la trace" revenait a montrer les premieres lignes d'un
  // journal ou l'erreur se trouve 1 h 30 plus loin.
  const [traceToutesLignes, setTraceToutesLignes] = useState(false);

  // Jusqu'ou les logs remontent, par environnement. Charge une fois : un
  // TraceId plus ancien ne ramenera rien, et proposer un lien mort est pire
  // que de ne rien proposer.
  const [retention, setRetention] = useState(null);
  useEffect(() => {
    let annule = false;
    logIntelligenceService.getLogsRetention()
      .then((r) => { if (!annule) setRetention(r); })
      // Silencieux : c'est un confort d'affichage, pas une donnee. Sans elle
      // on ne masque RIEN - mieux vaut un lien qui echoue qu'une information
      // cachee a tort.
      .catch(() => {});
    return () => { annule = true; };
  }, []);

  // `null` pour un environnement = on ne sait pas -> on ne masque pas.
  const traceEncoreDansElastic = useCallback((occ) => {
    const borne = retention?.[occ.env];
    if (!borne) return true;
    return occ.day >= String(borne).slice(0, 10);
  }, [retention]);

  const [lienCopie, setLienCopie] = useState(false);
  const copierLeLien = useCallback(async () => {
    const url = `${window.location.origin}/log-intelligence/signature/${encodeURIComponent(signatureId)}`;
    try {
      await navigator.clipboard.writeText(url);
      setLienCopie(true);
      setTimeout(() => setLienCopie(false), 2000);
    } catch {
      // clipboard refuse hors HTTPS ou sans permission : on montre l'URL
      // plutot que d'echouer en silence, l'utilisateur la copiera a la main.
      window.prompt('Copier ce lien :', url);
    }
  }, [signatureId]);

  // Un refus (401) s'affiche DANS la fenetre des logs, avec de quoi ressaisir
  // les identifiants - pas en rouvrant la modale d'office : la recherche s'y
  // deroule deja, et c'est la qu'on attend son resultat, quel qu'il soit.
  const chercherLogs = useCallback(async (traceId, env, identifiants, toutesLignes = false) => {
    setTrace({ id: traceId, env });
    setTraceLoading(true);
    setTraceResult(null);
    setTraceError(null);
    setTraceRefusee(false);
    setTraceToutesLignes(toutesLignes);
    try {
      const resp = await fetch('/api/logs/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ traceId, zone: env, auth: identifiants.token, onlyErrors: !toutesLignes })
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 401) {
        // Identifiants invalides : on les oublie, plutot que de les
        // representer a chaque clic.
        elasticAuthService.clearCredentials(env);
        setTraceRefusee(true);
        throw new Error(`Identifiants refusés par Elasticsearch ${env.toUpperCase()}. Chaque environnement a son propre mot de passe dans le Keepass.`);
      }
      if (!resp.ok) throw new Error(data.error || `Erreur ${resp.status}`);
      setTraceResult(data);
    } catch (e) {
      setTraceError(e.message || String(e));
    } finally {
      setTraceLoading(false);
    }
  }, []);

  const ouvrirTrace = useCallback((traceId, env, toutesLignes = false) => {
    const identifiants = elasticAuthService.getCredentials(env);
    if (!identifiants) {
      setTraceEnAttente({ id: traceId, env });
      setAuthZone(env);
      return;
    }
    chercherLogs(traceId, env, identifiants, toutesLignes);
  }, [chercherLogs]);

  const authentifier = useCallback(async (username, password) => {
    // Sans `await` sur la recherche : la modale se ferme des la validation et
    // la recherche se poursuit dans la fenetre des logs, au lieu que la modale
    // la recouvre jusqu'a la reponse.
    const env = authZone;
    elasticAuthService.saveCredentials(env, username, password);
    const enAttente = traceEnAttente;
    if (enAttente) {
      const identifiants = elasticAuthService.getCredentials(env);
      if (identifiants) chercherLogs(enAttente.id, enAttente.env, identifiants);
    }
  }, [authZone, traceEnAttente, chercherLogs]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    logIntelligenceService.getSignature(signatureId, { env })
      .then((data) => {
        if (cancelled) return;
        setDetail(data);
        // Sur un probleme groupe, le formulaire montre la valeur COMMUNE au
        // groupe, pas celle de la variante par laquelle on est entre : c'est
        // sur le groupe qu'on s'apprete a ecrire. Quand les variantes
        // divergent (`mixed`), on retombe sur celle qu'on a ouverte - et le
        // bandeau ambre dit que l'enregistrement les alignera.
        const groupe = probleme && probleme.signatureCount > 1 ? probleme : null;
        const commun = (valeurGroupe, valeurSignature) => (
          groupe && valeurGroupe && valeurGroupe !== 'mixed' ? valeurGroupe : valeurSignature
        );
        setOwnerTeam(commun(groupe?.ownerTeam, data.owner_team) || '');
        setWiId(data.wi_id || '');
        setStatus(commun(groupe?.status, data.status) || 'new');
        setMutedUntil(data.muted_until || '');
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [signatureId, env, probleme]);

  // Des qu'un probleme a plusieurs variantes, le triage est celui du
  // PROBLEME, d'ou qu'on l'ouvre. Offrir des champs par variante produisait
  // exactement ce qu'on a constate a l'ecran : une variante "Triee" a cote de
  // deux "Non triee" pour une seule et meme cause.
  const triageGroupe = Boolean(probleme && probleme.signatureCount > 1);
  const statutsDivergents = triageGroupe && probleme.status === 'mixed';
  const equipesDivergentes = triageGroupe && probleme.ownerTeam === 'mixed';

  const saveTriage = async () => {
    if (status === 'muted' && !mutedUntil) {
      setSaveError('Une échéance ("Muette jusqu\'au") est requise pour le statut "Muette" — jamais de mute sans échéance.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const valeurs = { ownerTeam, wiId, status, mutedUntil: status === 'muted' ? mutedUntil : null };

      if (triageGroupe) {
        // Le triage descend sur TOUTES les variantes, en une transaction cote
        // serveur. Une boucle ici pourrait s'arreter a mi-chemin et laisser le
        // groupe dans l'etat incoherent qu'on cherche a supprimer.
        await logIntelligenceService.updateProblem(probleme.id, filtres, valeurs);
        setDetail((prev) => ({ ...prev, owner_team: ownerTeam || null, wi_id: wiId || null, status, muted_until: valeurs.mutedUntil }));
        onUpdated?.({ id: signatureId, problemId: probleme.id, ...valeurs });
      } else {
        const updated = await logIntelligenceService.updateSignature(signatureId, valeurs);
        // Fusionne plutot que remplacer : le PATCH ne renvoie que la ligne
        // signature (owner_team/status/...), pas occurrences/suspectPrs -
        // un remplacement complet ferait planter le graphe au rendu suivant.
        setDetail((prev) => ({ ...prev, ...updated }));
        onUpdated?.(updated);
      }
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Le graphe se limite aux 7 derniers jours pour rester lisible - la table
  // en dessous, elle, garde tout l'historique (jamais de perte de donnee,
  // juste une vue recente par defaut).
  const CHART_DAYS = 7;
  const chartData = useMemo(() => {
    if (!detail) return [];
    const byDay = new Map();
    for (const occ of detail.occurrences) {
      byDay.set(occ.day, (byDay.get(occ.day) || 0) + occ.count);
    }
    return [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-CHART_DAYS)
      .map(([day, count]) => ({ day, count }));
  }, [detail]);

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Détail de la signature</h2>
          <div className="flex items-center gap-2">
            {/* Le lien a coller dans un ticket Azure DevOps. Il pointe sur
                CETTE signature et s'ouvre directement dessus, quels que
                soient les filtres de celui qui le recevra. */}
            <button
              onClick={copierLeLien}
              className="text-xs px-2 py-1 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 whitespace-nowrap"
              title="Copier l'URL de cette erreur, à coller dans un ticket"
            >
              {lienCopie ? 'Lien copié' : 'Copier le lien'}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading && <div className="text-gray-500">Chargement...</div>}
        {error && <div className="text-red-600">{error}</div>}

        {detail && (
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <StatusBadge status={detail.status} />
                <SeverityBadge severity={detail.ai_severity} />
                <span className="text-xs text-gray-400 font-mono">{detail.id.slice(0, 12)}</span>
                {detail.first_seen_build && (
                  /* Le build du service au moment de la premiere detection -
                     ce qu'un ticket de bug doit nommer. Il n'existe pas de
                     "version de l'environnement" : jusqu'a 14 builds
                     coexistent le meme jour sur un env, les composants etant
                     deployes independamment. */
                  <span
                    className="text-xs text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded"
                    title={`Build du service ${detail.service} au moment de la première détection`}
                  >
                    build {detail.first_seen_build}
                  </span>
                )}
              </div>
              {detail.ai_title && (
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-gray-900">{detail.ai_title}</span>
                  <AiBadge />
                </div>
              )}
              <div className={detail.ai_title ? 'text-sm text-gray-500' : 'font-semibold text-gray-900'}>{detail.exception_type}</div>
              <div className="text-sm text-gray-500">{detail.service} · {detail.top_frame || 'frame inconnue'}</div>
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Message</div>
              <div className="text-sm text-gray-800 bg-gray-50 rounded p-3 break-words">{detail.message_sample}</div>
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Cause sous-jacente</div>
              <CauseSousJacente brut={detail.inner_exceptions} />
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Analyse IA</div>
              {detail.ai_analyzed_at ? (
                <div className="text-sm text-gray-800 bg-gray-50 rounded p-3 space-y-1">
                  <div>
                    <span className="text-gray-500">Nature :</span> {detail.ai_nature} ·{' '}
                    <span className="text-gray-500">Catégorie :</span> {detail.ai_category} ·{' '}
                    <span className="text-gray-500">Composant :</span> {detail.ai_component || '—'}
                  </div>
                  {detail.ai_explanation && <div className="text-gray-700">{detail.ai_explanation}</div>}
                  <div className="text-xs text-gray-400">
                    Modèle {detail.ai_model} · {detail.ai_analyzed_at}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-400">
                  Pas encore analysée (aucune clé LLM Gateway configurée pour l'instant, ou signature pas encore vue comme nouvelle par un run).
                </div>
              )}
            </div>

            {detail.stack_sample && (
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Stack</div>
                <pre className="text-xs text-gray-700 bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto whitespace-pre-wrap">{detail.stack_sample}</pre>
              </div>
            )}

            <div>
              {/* L'intitule nomme le perimetre au lieu de l'annoncer faux :
                  il disait "toutes envs" alors que l'ecran entier etait filtre
                  sur un seul. */}
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Occurrences {env ? `sur ${env}` : '(tous environnements)'} — {CHART_DAYS} derniers jours d'activité
              </div>
              {chartData.length > 0 ? (
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="day" fontSize={11} />
                      <YAxis fontSize={11} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#2563eb" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-sm text-gray-400">Aucune occurrence.</div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-xs mt-3">
                  <thead>
                    <tr className="text-gray-500 text-left">
                      <th className="py-1">Env</th>
                      <th className="py-1">Jour</th>
                      {/* "Version" lisait occurrence.version, un champ que le
                          collecteur met a null en dur : la colonne affichait
                          "—" sur 100 % des lignes. Le build, lui, est
                          renseigne sur 99,9 % des occurrences. */}
                      <th className="py-1" title="Tag de l'image du conteneur qui a produit le log. Un seul build est conservé par jour : celui du dernier run de collecte.">Build ⓘ</th>
                      <th className="py-1 text-right">Compte</th>
                      <th className="py-1 pl-3">TraceId(s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Du plus RECENT au plus ancien : on ouvre cette fiche
                        pour savoir ou en est l'erreur aujourd'hui, pas pour
                        relire ses debuts. L'API rend l'ordre chronologique,
                        qui reste celui du graphique juste au-dessus. */}
                    {[...detail.occurrences].sort((a, b) => b.day.localeCompare(a.day)).map((occ, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="py-1">{occ.env}</td>
                        <td className="py-1">{occ.day}</td>
                        <td className="py-1 font-mono text-[11px] text-gray-600">{occ.build_id || '—'}</td>
                        <td className="py-1 text-right">{occ.count.toLocaleString('fr-FR')}</td>
                        <td className="py-1 pl-3 font-mono text-[11px] text-gray-500 break-all">
                          {parseTraceIds(occ.trace_ids).length === 0
                            ? <span title="Ce log applicatif ne portait pas de TraceId - rien a rechercher.">—</span>
                            : !traceEncoreDansElastic(occ)
                              ? (
                                /* Les identifiants existaient, mais Elasticsearch
                                   ne les a plus : les afficher reviendrait a
                                   proposer des liens morts. L'occurrence, elle,
                                   reste comptee - c'est l'historique. */
                                <span className="text-gray-400">
                                  <span className="italic">hors rétention</span>
                                  <span
                                    className="ml-1 text-gray-300 cursor-help"
                                    title={`${parseTraceIds(occ.trace_ids).length} TraceId enregistré(s), mais les logs de ${occ.env} ne remontent qu'au ${String(retention?.[occ.env] || '').slice(0, 10)} — Elasticsearch a effacé ceux-ci.`}
                                  >ⓘ</span>
                                </span>
                              )
                              : parseTraceIds(occ.trace_ids).map((id, j) => (
                              <React.Fragment key={id}>
                                {j > 0 && ', '}
                                {ZONES_LOGS.includes(occ.env) ? (
                                  <button
                                    onClick={() => ouvrirTrace(id, occ.env)}
                                    className="text-blue-600 hover:underline"
                                    title={`Voir les logs applicatifs de cette trace sur ${occ.env}`}
                                  >
                                    {id}
                                  </button>
                                ) : (
                                  <span title={`Le service de logs ne couvre pas ${occ.env} - recherche impossible depuis ici.`}>
                                    {id}
                                  </span>
                                )}
                              </React.Fragment>
                            ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Livré avant cette erreur</div>
              <PrsDeLaSignature detail={detail} />
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Tests en cours à ce moment-là</div>
              <CampagnesDeLaSignature campaigns={detail.campaigns} env={env} />
            </div>

            <div className="border-t border-gray-200 pt-4">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Statut &amp; assignation (manuels)
                {triageGroupe && (
                  <span className="ml-2 normal-case font-normal text-gray-400">
                    — s&apos;applique aux {probleme.signatureCount} variantes de ce problème
                  </span>
                )}
              </div>
              {(statutsDivergents || equipesDivergentes) && (
                /* L'ecrasement est assume, mais il ne doit pas etre une
                   surprise : on dit ce qui diverge avant d'enregistrer. */
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2">
                  Les variantes de ce problème n&apos;ont pas toutes
                  {statutsDivergents && ' le même statut'}
                  {statutsDivergents && equipesDivergentes && ' ni'}
                  {equipesDivergentes && ' la même équipe'}.
                  Enregistrer les alignera toutes sur ce que vous choisissez ici.
                </div>
              )}
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Statut</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="border border-gray-300 rounded-md text-sm px-2 py-1.5 min-w-[150px]"
                  >
                    {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
                {status === 'muted' && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Muette jusqu'au</label>
                    <input
                      type="date"
                      value={mutedUntil ? mutedUntil.slice(0, 10) : ''}
                      onChange={(e) => setMutedUntil(e.target.value ? `${e.target.value}T00:00:00.000Z` : '')}
                      className="border border-gray-300 rounded-md text-sm px-2 py-1.5"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Équipe</label>
                  <select
                    value={ownerTeam}
                    onChange={(e) => setOwnerTeam(e.target.value)}
                    className="border border-gray-300 rounded-md text-sm px-2 py-1.5 min-w-[180px]"
                  >
                    <option value="">{NO_TEAM}</option>
                    {teams.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Ticket de bug (ID Work Item)</label>
                  <input
                    value={wiId}
                    onChange={(e) => setWiId(e.target.value)}
                    placeholder="ex. 123456"
                    className="border border-gray-300 rounded-md text-sm px-2 py-1.5 w-40"
                  />
                </div>
                <button
                  onClick={saveTriage}
                  disabled={saving}
                  className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
                {detail.wi_id && (
                  <a
                    href={`${WORK_ITEM_BASE_URL}${encodeURIComponent(detail.wi_id)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                  >
                    Ouvrir #{detail.wi_id} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              {saveError && <div className="text-sm text-red-600 mt-2">{saveError}</div>}
            </div>
          </div>
        )}
      </div>

      {/* Les deux modales sont rendues DANS le fond du panneau, dont le onClick
          ferme le detail : sans cet arret, un clic dans un champ de la modale
          remontait jusqu'a lui et demontait le tout, modale comprise. */}
      <div onClick={(e) => e.stopPropagation()}>
      <ElasticAuthModal
        isOpen={Boolean(authZone)}
        onClose={() => { setAuthZone(null); setTraceEnAttente(null); }}
        onAuthenticate={authentifier}
        zone={authZone || ''}
      />

      {trace && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
          onClick={() => setTrace(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-4 border-b border-gray-200">
              <div>
                <div className="text-base font-semibold text-gray-900">Logs applicatifs de cette trace</div>
                <div className="text-xs text-gray-500 font-mono mt-0.5">{trace.id} · {trace.env}</div>
              </div>
              <button onClick={() => setTrace(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              {traceLoading && <div className="text-sm text-blue-600">Recherche dans les logs de {trace.env}…</div>}
              {!traceLoading && traceError && (
                <div className="text-sm text-red-600">
                  {traceError}
                  {traceRefusee && (
                    <button
                      onClick={() => { setTraceEnAttente({ id: trace.id, env: trace.env }); setAuthZone(trace.env); }}
                      className="block mt-2 text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Ressaisir les identifiants
                    </button>
                  )}
                </div>
              )}
              {!traceLoading && !traceError && traceResult && (() => {
                const hits = traceResult?.hits?.hits || [];
                const total = traceResult?.hits?.total?.value ?? hits.length;
                const tronque = hits.length < total;
                const basculer = () => ouvrirTrace(trace.id, trace.env, !traceToutesLignes);

                if (hits.length === 0) {
                  // Un vide doit dire POURQUOI, et le dire precisement quand
                  // on peut : le serveur joint la date du plus ancien log
                  // encore conserve, ce qui tranche entre "au-dela de la
                  // retention" et "cette trace n'existe pas ici".
                  const depuis = traceResult?.retentionFrom;
                  return (
                    <div className="text-sm text-gray-500 space-y-2">
                      <div>
                        {traceToutesLignes
                          ? <>Aucun log ne porte cette trace sur <span className="font-mono">{trace.env}</span>.</>
                          : <>Cette trace ne contient aucune ligne d'erreur sur <span className="font-mono">{trace.env}</span>.</>}
                      </div>
                      {depuis && (
                        <div className="text-xs">
                          Les logs de {trace.env} ne remontent qu'au{' '}
                          <span className="font-mono">{String(depuis).slice(0, 10)}</span> — au-delà, la rétention
                          Elasticsearch les a effacés.
                        </div>
                      )}
                      {!traceToutesLignes && (
                        <button onClick={basculer} className="text-sm text-blue-600 hover:underline">
                          Voir toutes les lignes de la trace
                        </button>
                      )}
                    </div>
                  );
                }

                return (
                  <>
                    <div className="text-xs text-gray-500 mb-2 flex items-center justify-between gap-4">
                      <span>
                        {traceToutesLignes
                          ? <>{hits.length.toLocaleString('fr-FR')} ligne{hits.length > 1 ? 's' : ''} affichée{hits.length > 1 ? 's' : ''} sur {total.toLocaleString('fr-FR')}, tous niveaux confondus.</>
                          : <>{hits.length.toLocaleString('fr-FR')} ligne{hits.length > 1 ? 's' : ''} d'erreur sur {total.toLocaleString('fr-FR')} pour cette trace.</>}
                        {tronque && ' Les plus anciennes d’abord.'}
                      </span>
                      <button onClick={basculer} className="text-blue-600 hover:underline shrink-0">
                        {traceToutesLignes ? 'Erreurs seulement' : 'Toutes les lignes'}
                      </button>
                    </div>
                    <div className="border border-gray-200 rounded">
                      {hits.map((hit, i) => <LigneDeLog key={hit._id || i} hit={hit} />)}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// Tri du tableau principal : rangs explicites pour severite/statut (l'ordre
// alphabetique brut ne correspond pas a un ordre de gravite/workflow utile),
// simple valeur brute pour les autres colonnes.
const SEVERITY_RANK = { blocker: 0, major: 1, minor: 2, noise: 3 };
const STATUS_RANK = { new: 0, triaged: 1, known_issue: 2, muted: 3, fixed: 4, wont_fix: 5 };

function sortValue(problem, key) {
  switch (key) {
    // Un probleme peut frapper plusieurs services : on trie sur le plus
    // touche, celui qui est affiche en clair dans la colonne.
    case 'service': return problem.services?.[0]?.service || '';
    case 'title': return (problem.title || problem.exceptionType || '').toLowerCase();
    case 'message': return (problem.messageSample || '').toLowerCase();
    // Trie sur ce que la cellule montre reellement, pas sur la valeur
    // plate : un groupe a deux equipes affiche les deux, il doit se
    // classer la ou on le lit.
    case 'owner_team': return (problem.ownerTeams?.valeurs || []).join(', ');
    case 'total_count': return problem.totalCount || 0;
    // On trie sur l'heure REELLE quand on l'a : c'est ce qui permet de
    // remettre deux erreurs du meme jour dans l'ordre.
    case 'first_seen': return problem.firstTs || problem.firstSeen || '';
    // Même principe pour la dernière erreur. Les deux formes se comparent
    // comme des chaînes ISO : « 2026-09-18 » (heure inconnue) tombe bien
    // dans sa journée, avant « 2026-09-18T04:12 », jamais dans une autre.
    case 'last_occurrence': return problem.lastTs || problem.lastDay || '';
    // Pas encore analysee par l'IA -> traitee comme la moins prioritaire,
    // peu importe le sens du tri (coherent avec l'affichage "—" du badge).
    case 'severity': return problem.severity in SEVERITY_RANK ? SEVERITY_RANK[problem.severity] : 99;
    case 'status': return problem.status in STATUS_RANK ? STATUS_RANK[problem.status] : 99;
    default: return '';
  }
}

// "ChapsVision.Universes.Exceptions.UniverseNotFoundException" ->
// "UniverseNotFoundException" : le namespace complet ne tient pas dans la
// colonne et n'apprend rien de plus.
function shortExceptionType(type) {
  return String(type || '').split('.').pop();
}

// Une panne de dependance externe leve plusieurs types d'exception pour un
// seul incident (Cassandra en leve 7 : NoHostAvailable, WriteTimeout...) :
// ils sont regroupes sous une famille "Cassandra.*". Afficher le type d'une
// seule des signatures serait trompeur - on montre la famille et le nombre.
function exceptionLabel(problem) {
  const family = problem.exceptionFamily || problem.exceptionType || '';
  // "Unknown" n'est pas un type d'exception nomme ainsi : c'est l'absence de
  // type (le log ne portait pas d'exception structuree). L'afficher tel quel
  // le faisait passer pour une vraie classe .NET.
  if (!family || family === 'Unknown') return 'type non renseigné';
  const label = family.endsWith('.*') ? family : shortExceptionType(family);
  return problem.distinctExceptionTypes > 1
    ? `${label} · ${problem.distinctExceptionTypes} types`
    : label;
}

// Titre d'une variante depliee : le titre IA quand il existe, sinon le type
// brut - et jamais le mot "Unknown", qui n'est pas un type mais son absence.
function signatureTitle(sig) {
  if (sig.ai_title) return sig.ai_title;
  return sig.exception_type && sig.exception_type !== 'Unknown'
    ? shortExceptionType(sig.exception_type)
    : 'type non renseigné';
}

function compareSortValues(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' });
}

function toDayIso(date) {
  return date.toISOString().slice(0, 10);
}

function daysAgoIso(n) {
  return toDayIso(new Date(Date.now() - n * 86400000));
}

// Bornes larges pour simuler "toute la periode" sans avoir besoin d'un
// sentinel dedie cote API (qui n'accepte que since/until concrets).
const ALL_TIME_SINCE = '2000-01-01';
const ALL_TIME_UNTIL = '2999-12-31';

// Bug remonte par l'utilisateur (03/09/2026) : le badge "Nouveau" etait
// calcule contre `sinceDay` (le filtre de periode affiche), donc une meme
// signature apparaissait/disparaissait selon la fenetre parcourue (visible
// en 14 jours, invisible en 7 jours pour une signature vieille de 10 jours)
// - un signal cense etre une propriete stable de la signature ne doit pas
// dependre d'un filtre d'affichage sans rapport. Fenetre fixe a la place,
// independante de tout filtre : 2 jours calendaires glissants.
const NEW_SIGNATURE_WINDOW_DAYS = 1;

const RANGE_PRESETS = [
  { key: '1d', label: "Aujourd'hui", days: 0 },
  { key: '7d', label: '7 derniers jours', days: 6 },
  { key: '14d', label: '14 derniers jours', days: 13 },
  { key: '30d', label: '30 derniers jours', days: 29 },
  { key: 'all', label: 'Toute la période', days: null }
];

const ACTIVE_ENV_STORAGE_KEY = 'logIntelligence.activeEnv';

// Le detail d'une signature a une URL a lui : /log-intelligence/signature/<id>.
//
// Demande d'un developpeur le 15/10/2026 : il cree des tickets de bug dans
// Azure DevOps et veut y coller un lien qui ouvre l'erreur. Jusqu'ici le
// detail n'existait que comme panneau ouvert par un clic - rien a copier,
// rien a coller dans un ticket.
//
// L'identifiant de signature est le bon ancrage : il survit aux filtres, a la
// periode et au regroupement. Il ne survit PAS a un refingerprint, qui
// recalcule les empreintes et fusionne des signatures - c'est une operation
// manuelle et rare, mais un lien pose aujourd'hui peut casser ce jour-la.
export default function LogIntelligencePage() {
  const { signatureId: signatureDeLUrl } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  // Une ligne = un "probleme" (un groupe de signatures partageant la meme
  // cause), plus une signature : en prod, 2070 signatures ne representent que
  // 578 problemes, dont trois erreurs Solr qui pesaient a elles seules 1111
  // lignes. C'est le regroupement que les developpeurs reclamaient en
  // rejetant la page ("les logs sont pleins de doublons").
  const [problems, setProblems] = useState([]);
  const [problemTotals, setProblemTotals] = useState({ total: 0, signatureTotal: 0 });
  // Detail d'un groupe : charge a la demande (deplier), puis garde en cache.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [groupSignatures, setGroupSignatures] = useState({});
  const [correlations, setCorrelations] = useState({});
  const [loadingGroupIds, setLoadingGroupIds] = useState(() => new Set());
  const [services, setServices] = useState([]);
  const [envs, setEnvs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedId, setSelectedId] = useState(signatureDeLUrl || null);
  // Le probleme d'ou le panneau a ete ouvert : c'est lui qui decide si le
  // triage porte sur une signature ou sur tout le groupe.
  const [selectedProbleme, setSelectedProbleme] = useState(null);

  // L'URL fait foi : on la suit quand elle change (retour arriere du
  // navigateur, lien colle dans la barre d'adresse).
  useEffect(() => { setSelectedId(signatureDeLUrl || null); }, [signatureDeLUrl]);

  // ... et on l'ecrit quand la selection change, pour que le lien soit
  // toujours copiable depuis la barre d'adresse.
  const ouvrirSignature = useCallback((id, probleme = null) => {
    setSelectedId(id);
    setSelectedProbleme(probleme);
    navigate(id ? `/log-intelligence/signature/${encodeURIComponent(id)}` : '/log-intelligence');
  }, [navigate]);

  // Quand on arrive par un lien, on ne sait pas encore a quel probleme la
  // signature appartient : on le retrouve des que la liste est chargee, pour
  // que le triage reste celui du GROUPE. Sans ca, un lien partage ramenerait
  // le triage par variante qu'on vient justement de supprimer.
  //
  // Si la signature est hors de la periode ou de l'environnement affiches,
  // aucun probleme ne la contient : le panneau s'ouvre seul et le triage
  // porte sur elle. C'est la seule chose honnete - on ne connait pas son
  // groupe dans ce perimetre.
  useEffect(() => {
    if (!selectedId || selectedProbleme || problems.length === 0) return;
    const trouve = problems.find((p) => (p.signatureIds || []).includes(selectedId));
    if (trouve) setSelectedProbleme(trouve);
  }, [selectedId, selectedProbleme, problems]);

  // Un seul env actif a la fois, jamais "tous" - persiste le choix pour la
  // session du navigateur. La valeur reelle est validee une fois que la
  // liste des envs actifs est chargee (voir l'effet plus bas).
  const [activeEnv, setActiveEnv] = useState(() => {
    try { return localStorage.getItem(ACTIVE_ENV_STORAGE_KEY) || null; } catch { return null; }
  });

  const [serviceFilter, setServiceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Tri du tableau (clic sur un en-tete) - null = ordre par defaut renvoye
  // par l'API (SORTS.recent : jour de la derniere erreur, puis l'heure quand
  // elle est connue), inchange jusqu'a ce qu'une colonne soit cliquee.
  // Ce n'est plus last_seen : cette date-la est celle du RUN qui a collecte
  // l'erreur, et elle differe du jour de l'erreur sur 438 signatures sur 748.
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  // Intervalle de temps recherché - un preset remplit juste les deux dates,
  // qui restent modifiables individuellement pour une periode sur mesure.
  const [sinceDay, setSinceDay] = useState(daysAgoIso(6));
  const [untilDay, setUntilDay] = useState(daysAgoIso(0));

  // Les campagnes de test. Repliees par defaut : la page repond d'abord a
  // « quelles erreurs ? ». Une seule campagne depliee a la fois - deux listes
  // d'erreurs ouvertes cote a cote ne se comparent pas, elles se melangent.
  const [campagnes, setCampagnes] = useState([]);
  const [campagnesOuvertes, setCampagnesOuvertes] = useState(false);
  const [campagneDepliee, setCampagneDepliee] = useState(null);
  const [signaturesParCampagne, setSignaturesParCampagne] = useState({});
  const [campagneEnCharge, setCampagneEnCharge] = useState(null);

  const applyPreset = (key) => {
    const preset = RANGE_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    setUntilDay(daysAgoIso(0));
    setSinceDay(preset.days === null ? ALL_TIME_SINCE : daysAgoIso(preset.days));
  };

  const selectEnv = (key) => {
    setActiveEnv(key);
    try { localStorage.setItem(ACTIVE_ENV_STORAGE_KEY, key); } catch { /* stockage indisponible, tant pis */ }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [envsRes, servicesRes, teamsRes] = await Promise.all([
        logIntelligenceService.getEnvs(),
        logIntelligenceService.getServices(),
        logIntelligenceService.getTeams()
      ]);
      const enabledEnvs = envsRes.filter((e) => e.enabled);
      setEnvs(enabledEnvs);
      setServices(servicesRes);
      setTeams(teamsRes);

      // Valide/initialise l'env actif une fois la vraie liste connue - si le
      // choix persiste (localStorage) n'est plus un env actif, on retombe
      // sur le premier disponible plutot que d'interroger un env fantome.
      const validEnv = enabledEnvs.some((e) => e.key === activeEnv) ? activeEnv : (enabledEnvs[0]?.key || null);
      if (validEnv !== activeEnv) selectEnv(validEnv);
      if (!validEnv) { setLoading(false); return; }

      const range = { since: sinceDay, until: untilDay === ALL_TIME_UNTIL ? undefined : untilDay, env: validEnv };
      // Service et statut sont filtres PAR LE MOTEUR, pas ici : un groupe
      // peut contenir des signatures de statuts differents, donc filtrer sur
      // le statut agrege de la ligne ("mixed") masquerait des groupes dont
      // la majorite des signatures correspond pourtant au filtre. Filtrer en
      // amont laisse aussi le moteur recalculer les compteurs du groupe sur
      // les seules signatures retenues - sinon le total affiche ne
      // correspondrait plus au detail deplie.
      const [statsRes, runsRes, problemsRes] = await Promise.all([
        logIntelligenceService.getStats(range),
        logIntelligenceService.getRuns(5),
        logIntelligenceService.getProblems({
          ...range,
          service: serviceFilter === 'all' ? undefined : serviceFilter,
          status: statusFilter === 'all' ? undefined : statusFilter
        })
      ]);
      setStats(statsRes);
      setRuns(runsRes.filter((r) => r.env === validEnv));
      setProblems(problemsRes.problems);
      setProblemTotals({ total: problemsRes.total, signatureTotal: problemsRes.signatureTotal });
      // Les groupes deplies et leur detail valaient pour l'ancienne periode
      // ou l'ancien env : les garder afficherait des compteurs qui ne
      // correspondent plus a la ligne parente.
      setExpandedIds(new Set());
      setGroupSignatures({});
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinceDay, untilDay, activeEnv, serviceFilter, statusFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Les campagnes suivent les mêmes filtres de période et d'environnement que
  // la liste des problèmes. Chargées séparément : un échec de cette requête ne
  // doit pas vider la page principale, qui reste le cœur de l'écran.
  useEffect(() => {
    let annule = false;
    logIntelligenceService
      .getCampaigns({ since: sinceDay, until: untilDay, env: activeEnv === 'all' ? undefined : activeEnv })
      .then(({ campaigns }) => { if (!annule) setCampagnes(campaigns || []); })
      .catch(() => { if (!annule) setCampagnes([]); });
    return () => { annule = true; };
  }, [sinceDay, untilDay, activeEnv]);

  // Les erreurs d'une campagne sont chargées au dépliage, puis gardées : la
  // même campagne rouverte ne redemande rien.
  const handleToggleCampagne = useCallback((id) => {
    setCampagneDepliee((courante) => {
      if (courante === id) return null;
      if (!signaturesParCampagne[id]) {
        setCampagneEnCharge(id);
        logIntelligenceService.getCampaignSignatures(id)
          .then(({ signatures }) => setSignaturesParCampagne((m) => ({ ...m, [id]: signatures || [] })))
          .catch(() => setSignaturesParCampagne((m) => ({ ...m, [id]: [] })))
          .finally(() => setCampagneEnCharge((c) => (c === id ? null : c)));
      }
      return id;
    });
  }, [signaturesParCampagne]);

  const lastRun = runs[0] || null;

  // Severite et recherche restent cote client (pas d'equivalent cote API, et
  // ca garde la frappe instantanee) ; service et statut sont deja appliques
  // en amont par le moteur, cf. loadAll.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return problems.filter((problem) => {
      if (severityFilter !== 'all' && problem.severity !== severityFilter) return false;
      if (!term) return true;

      // Le texte, comme avant.
      if (`${problem.exceptionType} ${problem.title || ''} ${problem.messageSample}`.toLowerCase().includes(term)) return true;

      // Et les IDENTIFIANTS, qui ne marchaient pas (remonte le 15/10/2026
      // par un developpeur qui créait des tickets). Trois formes, parce que
      // ce sont les trois qu'on a sous la main quand on revient d'ailleurs :
      //
      //   - l'identifiant d'une variante, celui qu'on a collé dans le ticket ;
      //   - le numéro du ticket ADO, pour faire le chemin inverse ;
      //   - l'identifiant du problème, celui que porte une URL partagée.
      //
      // Comparaison par préfixe sur les identifiants de signature : ils font
      // 40 caractères, personne n'en recopie la totalité à la main.
      if ((problem.signatureIds || []).some((id) => id.toLowerCase().startsWith(term))) return true;
      if ((problem.wiIds?.valeurs || []).some((id) => String(id).toLowerCase() === term.replace(/^#/, ''))) return true;
      if (problem.id.toLowerCase().startsWith(term)) return true;

      return false;
    });
  }, [problems, severityFilter, search]);

  const sorted = useMemo(() => {
    if (!sortBy) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => dir * compareSortValues(sortValue(a, sortBy), sortValue(b, sortBy)));
  }, [filtered, sortBy, sortDir]);

  const sortIndicator = (key) => (
    sortBy === key ? <span className="text-gray-400 ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span> : null
  );

  // Deplier un groupe : son detail n'est charge qu'une fois, puis garde en
  // cache (il est vide par la liste au changement de periode/env).
  const toggleExpanded = useCallback(async (problem) => {
    const alreadyOpen = expandedIds.has(problem.id);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (alreadyOpen) next.delete(problem.id); else next.add(problem.id);
      return next;
    });
    // Deja charge : variantes pour un groupe, correlation pour une signature
    // unique (qui n'a pas de variante et ne remplirait donc jamais
    // groupSignatures - sans ce second test, chaque ouverture relancerait la
    // requete de correlation).
    const dejaCharge = problem.signatureCount > 1 ? groupSignatures[problem.id] : correlations[problem.id];
    if (alreadyOpen || dejaCharge) return;

    const filtres = {
      env: activeEnv,
      since: sinceDay,
      until: untilDay === ALL_TIME_UNTIL ? undefined : untilDay,
      service: serviceFilter === 'all' ? undefined : serviceFilter,
      status: statusFilter === 'all' ? undefined : statusFilter
    };

    // La corrélation est chargée en parallèle des variantes, et son échec ne
    // doit jamais priver des variantes : c'est un complément d'enquête, pas le
    // contenu de la ligne.
    logIntelligenceService.getProblemCorrelation(problem.id, filtres)
      .then((correlation) => setCorrelations((prev) => ({ ...prev, [problem.id]: correlation })))
      .catch((err) => setCorrelations((prev) => ({ ...prev, [problem.id]: { error: err.message } })));

    // Un probleme a signature unique n'a pas de variante a charger : la
    // depliee ne montre que la correlation, deja demandee ci-dessus.
    if (problem.signatureCount <= 1) return;

    setLoadingGroupIds((prev) => new Set(prev).add(problem.id));
    try {
      const rows = await logIntelligenceService.getProblemSignatures(problem.id, filtres);
      setGroupSignatures((prev) => ({ ...prev, [problem.id]: rows }));
    } catch (err) {
      setGroupSignatures((prev) => ({ ...prev, [problem.id]: { error: err.message } }));
    } finally {
      setLoadingGroupIds((prev) => {
        const next = new Set(prev);
        next.delete(problem.id);
        return next;
      });
    }
  }, [expandedIds, groupSignatures, correlations, activeEnv, sinceDay, untilDay, serviceFilter, statusFilter]);

  // Le PATCH ne renvoie que la ligne signature (ni total_count ni
  // occurrences), d'ou le spread partiel plutot qu'un remplacement.
  const handleSignatureUpdated = (updated) => {
    // Un triage de PROBLEME touche toutes ses variantes d'un coup : la mise a
    // jour locale doit suivre, sinon la liste continuerait d'afficher les
    // anciens statuts jusqu'au prochain chargement.
    if (updated.problemId) {
      const commun = {
        owner_team: updated.ownerTeam || null,
        wi_id: updated.wiId || null,
        status: updated.status,
        muted_until: updated.mutedUntil || null
      };
      setGroupSignatures((prev) => ({
        ...prev,
        [updated.problemId]: Array.isArray(prev[updated.problemId])
          ? prev[updated.problemId].map((s) => ({ ...s, ...commun }))
          : prev[updated.problemId]
      }));
      setProblems((prev) => prev.map((problem) => (problem.id === updated.problemId
        ? {
          ...problem,
          status: updated.status,
          ownerTeam: updated.ownerTeam || null,
          wiId: updated.wiId || null,
          // Le triage de groupe aligne TOUTES les variantes : la couverture
          // redevient donc totale, et le "n/N" disparait. Sans ces deux
          // lignes la liste garderait l'ancien partage jusqu'au prochain
          // chargement.
          wiIds: { valeurs: updated.wiId ? [updated.wiId] : [], portees: updated.wiId ? problem.signatureCount : 0, total: problem.signatureCount },
          ownerTeams: { valeurs: updated.ownerTeam ? [updated.ownerTeam] : [], portees: updated.ownerTeam ? problem.signatureCount : 0, total: problem.signatureCount }
        }
        : problem)));
      return;
    }

    setGroupSignatures((prev) => {
      const next = {};
      for (const [problemId, rows] of Object.entries(prev)) {
        next[problemId] = Array.isArray(rows)
          ? rows.map((s) => (s.id === updated.id ? { ...s, ...updated } : s))
          : rows;
      }
      return next;
    });
    // Le statut agrege de la ligne parente peut changer avec celui d'une de
    // ses signatures - recalcule sur le detail deja charge, sinon la ligne
    // afficherait un statut que son propre detail contredit.
    setProblems((prev) => prev.map((problem) => {
      const rows = groupSignatures[problem.id];
      if (!Array.isArray(rows) || !rows.some((s) => s.id === updated.id)) return problem;
      const statuses = [...new Set(rows.map((s) => (s.id === updated.id ? updated.status : s.status)).filter(Boolean))];
      return { ...problem, status: statuses.length === 1 ? statuses[0] : 'mixed' };
    }));
  };

  // Conteneur plus large que le max-w-7xl du reste du dashboard : cette page
  // est un tableau dense a 8 colonnes, dont Message et Service n'avaient
  // jamais assez de place. 1600px laisse respirer sans devenir illisible sur
  // un ecran ultra-large.
  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-900">Log Intelligence</h1>
        <button
          onClick={loadAll}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-300 rounded-md"
        >
          <RefreshCcw className="w-3.5 h-3.5" /> Rafraîchir
        </button>
      </div>

      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {envs.map((e) => (
          <button
            key={e.key}
            onClick={() => selectEnv(e.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeEnv === e.key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {e.key}
          </button>
        ))}
        {envs.length === 0 && !loading && (
          <div className="px-4 py-2 text-sm text-gray-400">Aucun environnement actif.</div>
        )}
      </div>

      {loadError && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-800">Impossible de charger Log Intelligence</div>
            <div className="text-sm text-red-700">{loadError}</div>
          </div>
        </div>
      )}

      {lastRun && lastRun.status === 'error' && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-800">Le dernier run de collecte a échoué ({lastRun.env})</div>
            <div className="text-sm text-amber-700">{lastRun.error}</div>
            <div className="text-xs text-amber-600 mt-1">
              Ceci n'est pas "aucune erreur" — la collecte elle-même n'a pas pu regarder les logs.
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-4 bg-white border border-gray-200 rounded-lg p-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Période</label>
          <select
            onChange={(e) => applyPreset(e.target.value)}
            defaultValue="7d"
            className="border border-gray-300 rounded-md text-sm px-2 py-1.5"
          >
            {RANGE_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Du</label>
          <input
            type="date"
            value={sinceDay === ALL_TIME_SINCE ? '' : sinceDay}
            onChange={(e) => setSinceDay(e.target.value || ALL_TIME_SINCE)}
            className="border border-gray-300 rounded-md text-sm px-2 py-1.5"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Au</label>
          <input
            type="date"
            value={untilDay === ALL_TIME_UNTIL ? '' : untilDay}
            onChange={(e) => setUntilDay(e.target.value || ALL_TIME_UNTIL)}
            className="border border-gray-300 rounded-md text-sm px-2 py-1.5"
          />
        </div>
      </div>

      {loading && !stats ? (
        <div className="text-gray-500">Chargement...</div>
      ) : stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          <StatCard label="Signatures actives (période)" value={stats.activeSignatures.toLocaleString('fr-FR')} />
          <StatCard label="Occurrences (période)" value={stats.occurrencesInRange.toLocaleString('fr-FR')} />
          <StatCard
            label="Par service (période)"
            value={stats.byService.length}
            sub={stats.byService.map((s) => `${s.service}: ${s.n.toLocaleString('fr-FR')}`).join(' · ') || 'aucun'}
          />
        </div>
      )}

      {lastRun && (
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
          <Clock className="w-3.5 h-3.5" />
          Dernière collecte : {lastRun.env} · {lastRun.status === 'ok' ? 'ok' : 'échec'} · {lastRun.finished_at || lastRun.started_at}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Type, message, id de signature ou n° de ticket…"
            className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-sm w-72"
          />
        </div>
        <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} className="border border-gray-300 rounded-md text-sm px-2 py-1.5">
          <option value="all">Tous les services</option>
          {services.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-300 rounded-md text-sm px-2 py-1.5">
          <option value="all">Tous les statuts</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} className="border border-gray-300 rounded-md text-sm px-2 py-1.5">
          <option value="all">Toutes sévérités</option>
          {Object.entries(SEVERITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>

      <div className="flex items-baseline gap-2 mb-2 text-sm text-gray-600">
        <span className="font-medium text-gray-900">{sorted.length.toLocaleString('fr-FR')} problème{sorted.length > 1 ? 's' : ''}</span>
        {problemTotals.signatureTotal > problemTotals.total && (
          <span className="text-xs text-gray-500">
            ({problemTotals.signatureTotal.toLocaleString('fr-FR')} signatures regroupées — une ligne par cause, pas par variante)
          </span>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          {/* table-fixed + des largeurs EN POURCENTAGE dont la somme fait
              exactement 100% : le tableau ne peut alors jamais depasser son
              conteneur, quelle que soit la largeur de l'ecran - donc jamais
              de scroll horizontal.
              Les versions precedentes melangeaient des largeurs en pixels
              (w-20, w-24, w-28) : leur somme etant fixe, elle finissait par
              depasser le conteneur sur un ecran etroit et ramenait le scroll
              horizontal que les PR 30991/30993 cherchaient a supprimer.
              Le prix reste la troncature (ellipsis) du texte qui ne tient
              pas - accepte explicitement, le clic ouvre le detail complet.
              Si une colonne est elargie, une autre DOIT etre reduite
              d'autant. */}
          <table className="w-full table-fixed text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              {/* 11 + 18 + 18 + 10 + 7 + 9 + 10 + 9 + 8 = 100%.
                  Service a 11% : le plus long nom reel est "argonos_common"
                  (14 caracteres, ~110px), et 11% d'un ecran portable 1366px
                  laisse ~114px de texte une fois le padding retire.
                  Les deux colonnes de dates restent cote a cote : on les lit
                  ensemble ("vue depuis longtemps, encore active aujourd'hui"),
                  les separer obligerait a traverser la ligne. */}
              <th className="px-4 py-2 w-[11%] whitespace-nowrap cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('service')}>
                Service{sortIndicator('service')}
              </th>
              <th className="px-4 py-2 w-[18%] cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('title')}>
                <span className="inline-flex items-center gap-1">
                  Type d'exception{sortIndicator('title')}
                  <AiBadge />
                </span>
              </th>
              <th className="px-4 py-2 w-[18%] cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('message')}>
                Message{sortIndicator('message')}
              </th>
              <th className="px-4 py-2 w-[10%] cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('owner_team')}>
                Équipe{sortIndicator('owner_team')}
              </th>
              <th className="px-4 py-2 w-[7%] text-right whitespace-nowrap cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('total_count')}>
                Occurrences{sortIndicator('total_count')}
              </th>
              <th
                className="px-4 py-2 w-[9%] whitespace-nowrap cursor-pointer select-none hover:text-gray-700"
                title="Date a laquelle l'outil a catalogue cette signature pour la premiere fois - PAS necessairement sa premiere apparition reelle en prod, seulement depuis quand on la surveille."
                onClick={() => toggleSort('first_seen')}
              >
                Détecté depuis <span className="text-gray-300">ⓘ</span>{sortIndicator('first_seen')}
              </th>
              {/* Pas de whitespace-nowrap : l'intitule tient sur deux lignes
                  dans l'en-tete plutot que de deborder sur la colonne voisine. */}
              <th
                className="px-4 py-2 w-[10%] cursor-pointer select-none hover:text-gray-700"
                title="Date de la derniere erreur REELLE, dans le perimetre filtre (environnement et periode) - et non la date du run qui l'a collectee. Une erreur ancienne encore active aujourd'hui remonte donc ici, meme si elle n'est pas nouvelle."
                onClick={() => toggleSort('last_occurrence')}
              >
                Dernière occurrence <span className="text-gray-300">ⓘ</span>{sortIndicator('last_occurrence')}
              </th>
              <th className="px-4 py-2 w-[9%] whitespace-nowrap cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('severity')}>
                <span className="inline-flex items-center gap-1">
                  Sévérité{sortIndicator('severity')}
                  <AiBadge />
                </span>
              </th>
              <th className="px-4 py-2 w-[8%] whitespace-nowrap cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('status')}>
                Statut{sortIndicator('status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((problem) => {
              const isNewProblem = (problem.firstTs || problem.firstSeen) >= daysAgoIso(NEW_SIGNATURE_WINDOW_DAYS);
              const isGroup = problem.signatureCount > 1;
              const isExpanded = expandedIds.has(problem.id);
              const rows = groupSignatures[problem.id];
              return (
                <React.Fragment key={problem.id}>
                  <tr
                    // Toute ligne se deplie, groupe ou non. La condition
                    // "seulement si plusieurs variantes" datait du moment ou
                    // deplier ne montrait que les variantes : avec une seule
                    // signature il n'y avait rien a voir. Depuis que le
                    // depliage porte la correlation (quel deploiement a precede
                    // l'apparition, quelles PR il livrait), elle privait de
                    // cette information les problemes les plus simples - ceux,
                    // justement, ou une cause unique est la plus probable.
                    onClick={() => toggleExpanded(problem)}
                    className={`border-t border-gray-100 hover:bg-blue-50 cursor-pointer ${isNewProblem ? 'bg-emerald-50/60' : ''}`}
                  >
                    {/* Une panne d'infra frappe plusieurs services a la fois :
                        on montre le plus touche, et le nombre des autres. Le
                        detail par service est dans les variantes depliees. */}
                    <td className="px-4 py-2 text-gray-700 truncate" title={(problem.services || []).map((s) => `${s.service} (${s.totalCount})`).join('\n')}>
                      {problem.services?.[0]?.service}
                      {problem.services?.length > 1 && (
                        <span className="ml-1 text-[10px] font-semibold text-slate-600 bg-slate-100 px-1 py-0.5 rounded align-[1px]">
                          +{problem.services.length - 1}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-900 font-medium">
                      <div className="truncate">
                        {isExpanded
                          ? <ChevronDown className="w-3.5 h-3.5 inline-block mr-1 text-gray-400 align-[-2px]" />
                          : <ChevronRight className="w-3.5 h-3.5 inline-block mr-1 text-gray-400 align-[-2px]" />}
                        {isNewProblem && (
                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1 py-0.5 rounded mr-1" title="Premiere fois cataloguee dans les 2 derniers jours - independant de la periode affichee ci-dessus">
                            Nouveau
                          </span>
                        )}
                        {problem.title || problem.exceptionType}
                      </div>
                      {/* Deux problemes peuvent porter le meme message et ne
                          differer QUE par le type d'exception (ex. le meme job
                          Hangfire qui echoue tantot en ArgumentException,
                          tantot en DbUpdateConcurrencyException). Sans ce
                          rappel, le titre IA masque le type et les deux lignes
                          semblent identiques - donc dupliquees, alors qu'elles
                          sont deux causes distinctes. */}
                      {problem.title && (
                        <div className="text-[11px] font-normal text-gray-400 truncate" title={problem.exceptionType}>
                          {exceptionLabel(problem)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600 truncate">
                      {isGroup && (
                        <span
                          className="text-[10px] font-semibold text-blue-700 bg-blue-100 px-1 py-0.5 rounded mr-1.5 align-[1px]"
                          title={`${problem.signatureCount} signatures ne differant que par une valeur (identifiant, duree...) - cliquer pour les deplier`}
                        >
                          {problem.signatureCount} variantes
                        </span>
                      )}
                      {problem.messageSample}
                    </td>
                    <td className="px-4 py-2 text-gray-700 truncate">
                      <EquipeAssignee partage={problem.ownerTeams} vide={NO_TEAM} />
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {problem.totalCount.toLocaleString('fr-FR')}
                        {/* La règle vit dans src/db/spike.js, pas ici : un pic
                            suppose une habitude, donc une erreur qui vient
                            d'apparaître n'en a pas. Le filtrer à l'affichage
                            aurait fait deux règles pour une seule question —
                            et c'est déjà arrivé, avec 7 badges sur 13 qui
                            contredisaient leurs propres chiffres. */}
                        {problem.isSpike && (
                          <span
                            className="text-red-600"
                            title={
                              problem.prev7Sum > 0
                                // "0 en moyenne" se lisait comme "jamais arrivee avant" - or ca
                                // ne veut dire que "aucune occurrence CETTE semaine-la", le
                                // probleme peut tres bien etre plus ancien (resurgence).
                                ? `Pic detecte : ${problem.todayCount.toLocaleString('fr-FR')} aujourd'hui vs ~${Math.round(problem.prev7Sum / 7).toLocaleString('fr-FR')} en moyenne les 7 jours precedents`
                                : `Pic detecte : ${problem.todayCount.toLocaleString('fr-FR')} aujourd'hui, aucune occurrence les 7 jours precedents (pas forcement une premiere apparition absolue - voir "Détecté depuis")`
                            }
                          >
                            📈
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap"><Horodatage reel={problem.firstTs} dateDuRun={problem.firstSeen} /></td>
                    <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap">
                      <Horodatage reel={problem.lastTs} dateDuRun={problem.lastDay} quoi="Dernière erreur" />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap"><SeverityBadge severity={problem.severity} /></td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {problem.status === 'mixed'
                        ? <span className="text-xs text-gray-500 italic" title="Les signatures de ce probleme n'ont pas toutes le meme statut - deplier pour les voir">plusieurs</span>
                        : <StatusBadge status={problem.status} />}
                      <TicketLie partage={problem.wiIds} />
                    </td>
                  </tr>

                  {isExpanded && (loadingGroupIds.has(problem.id) || rows?.error) && (
                    <tr className="bg-gray-50">
                      <td colSpan={9} className="px-4 py-2 text-xs border-t border-gray-100">
                        {loadingGroupIds.has(problem.id)
                          ? <span className="text-gray-500">Chargement des variantes...</span>
                          : <span className="text-red-600">{rows.error}</span>}
                      </td>
                    </tr>
                  )}

                  {isExpanded && isGroup && Array.isArray(rows) && (
                    <tr className="bg-gray-50">
                      <td colSpan={9} className="px-4 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        {problem.signatureCount} signatures regroupées
                        {problem.distinctTitles > 1 && ` · ${problem.distinctTitles} formulations différentes du titre IA pour cette même erreur`}
                        {/* Le triage est celui du probleme : il doit etre
                            atteignable depuis le groupe, pas seulement en
                            passant par une variante prise au hasard. */}
                        <button
                          onClick={(e) => { e.stopPropagation(); ouvrirSignature(problem.representativeSignatureId, problem); }}
                          className="ml-2 text-blue-600 hover:underline"
                        >
                          Trier ce problème
                        </button>
                      </td>
                    </tr>
                  )}

                  {/* Un probleme a signature unique n'a pas de variante a
                      montrer - la repeter sous elle-meme n'apprendrait rien.
                      Mais le detail doit rester a un clic, puisque la ligne
                      ouvre maintenant le depliage au lieu du panneau. */}
                  {isExpanded && !isGroup && (
                    <tr className="bg-gray-50">
                      <td colSpan={9} className="px-4 py-1.5 text-xs border-t border-gray-100">
                        <button
                          onClick={(e) => { e.stopPropagation(); ouvrirSignature(problem.representativeSignatureId, problem); }}
                          className="text-blue-600 hover:underline"
                        >
                          Ouvrir le détail de la signature
                        </button>
                      </td>
                    </tr>
                  )}

                  {isExpanded && (
                    <tr className="bg-gray-50">
                      <td colSpan={9} className="px-4 pb-2 pt-0.5 border-b border-gray-100">
                        <DeploymentCorrelation state={correlations[problem.id]} />
                      </td>
                    </tr>
                  )}

                  {/* De vraies lignes de tableau, et non un bloc dans une
                      cellule colSpan : c'est la seule facon que chaque valeur
                      d'une variante tombe sous la colonne qui la nomme.
                      Premiere colonne laissee vide, avec un simple rail a
                      gauche : le service y serait repete a l'identique pour
                      chaque variante, et l'empreinte de la signature (essayee
                      d'abord) se lisait comme une donnee metier alors que
                      c'est un detail technique - elle reste dans le panneau
                      de detail. Ce qui distingue une variante, c'est son
                      message, deja en colonne 3. */}
                  {isExpanded && Array.isArray(rows) && rows.map((sig) => (
                    <tr
                      key={sig.id}
                      onClick={() => ouvrirSignature(sig.id, problem)}
                      className="bg-gray-50/60 hover:bg-blue-50 cursor-pointer text-xs"
                    >
                      {/* La colonne retrouve un sens ici : depuis qu'un
                          probleme peut frapper plusieurs services, c'est la
                          variante qui dit lequel. */}
                      <td className="px-4 py-1.5 border-l-2 border-blue-200 text-gray-500 truncate">{sig.service}</td>
                      <td className="px-4 py-1.5 text-gray-600 truncate">{signatureTitle(sig)}</td>
                      <td className="px-4 py-1.5 text-gray-600 truncate">{sig.message_sample}</td>
                      <td className="px-4 py-1.5 text-gray-600 truncate">
                        {sig.owner_team || <span className="text-gray-400">{NO_TEAM}</span>}
                      </td>
                      <td className="px-4 py-1.5 text-right text-gray-600 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          {(sig.total_count || 0).toLocaleString('fr-FR')}
                          {sig.is_spike && <span className="text-red-600" title="Pic detecte sur cette variante">📈</span>}
                        </span>
                      </td>
                      <td className="px-4 py-1.5 text-gray-500 whitespace-nowrap"><Horodatage reel={sig.first_ts} dateDuRun={sig.first_seen} /></td>
                      {/* Les champs de la variante, pas ceux du probleme :
                          c'est precisement ce qu'on vient chercher en
                          depliant - laquelle est encore active. */}
                      <td className="px-4 py-1.5 text-gray-500 whitespace-nowrap">
                        <Horodatage reel={sig.last_occurrence_ts} dateDuRun={sig.last_day} quoi="Dernière erreur" />
                      </td>
                      <td className="px-4 py-1.5 whitespace-nowrap"><SeverityBadge severity={sig.ai_severity} /></td>
                      <td className="px-4 py-1.5 whitespace-nowrap">
                        <StatusBadge status={sig.status} />
                        <TicketLie partage={{ valeurs: sig.wi_id ? [sig.wi_id] : [], portees: sig.wi_id ? 1 : 0, total: 1 }} />
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {sorted.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                  Aucun problème ne correspond aux filtres.
                </td>
              </tr>
            )}
          </tbody>
          </table>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mt-2">
        <span className="inline-flex items-center gap-1"><span className="bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded font-semibold">Nouveau</span> = première fois cataloguée dans les 2 derniers jours (peu importe la période affichée)</span>
        <span className="inline-flex items-center gap-1"><AiBadge /> = titre reformulé par l'IA (le type brut peut être identique à d'autres lignes)</span>
        <span className="inline-flex items-center gap-1"><span className="bg-blue-100 text-blue-700 px-1 py-0.5 rounded font-semibold">n variantes</span> = signatures ne différant que par une valeur (identifiant, durée...) — cliquer la ligne pour les déplier</span>
        <span className="inline-flex items-center gap-1"><span className="bg-slate-100 text-slate-600 px-1 py-0.5 rounded font-semibold">+n</span> = le problème touche n services de plus (une panne d'infra se voit depuis tous ceux qui en dépendent) — le détail est dans les variantes</span>
        <span className="inline-flex items-center gap-1">📈 = pic aujourd'hui : au moins 3× la moyenne des 7 jours précédents, ou une résurgence après 7 jours sans occurrence. Jamais sur une erreur nouvelle — « Nouveau » le dit déjà.</span>
      </div>

      {/* Le sens inverse, replié par défaut : la page répond d'abord à
          « quelles erreurs ? », et celle-ci répond à « pendant quel test ? ».
          Les deux ne se lisent pas en même temps. */}
      <div className="mt-8">
        <button
          onClick={() => setCampagnesOuvertes((v) => !v)}
          className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-blue-700"
        >
          {campagnesOuvertes ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Campagnes de test de la période
          {campagnes.length > 0 && <span className="text-gray-400 font-normal">({campagnes.length})</span>}
        </button>

        {campagnesOuvertes && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mt-3">
            {campagnes.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-500">
                Aucune campagne ingérée sur cette période.
                <div className="text-xs text-gray-400 mt-1">
                  Les campagnes sont lues sur les pipelines des dossiers Test/Argonos 26.1 et 26.2. Celles
                  qui visent un environnement non collecté (val05, val9) ne sont pas suivies.
                </div>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="w-8" />
                    <th className="px-4 py-2 text-left font-semibold">Suite</th>
                    <th className="px-4 py-2 text-left font-semibold">Branche</th>
                    <th className="px-4 py-2 text-left font-semibold">Env</th>
                    <th className="px-4 py-2 text-left font-semibold">Exécution</th>
                    <th className="px-4 py-2 text-left font-semibold">Résultat</th>
                    <th className="px-4 py-2 text-right font-semibold">Erreurs</th>
                  </tr>
                </thead>
                <tbody>
                  {campagnes.map((c) => (
                    <LigneCampagne
                      key={c.id}
                      campagne={c}
                      ouverte={campagneDepliee === c.id}
                      onToggle={handleToggleCampagne}
                      signatures={signaturesParCampagne[c.id]}
                      chargement={campagneEnCharge === c.id}
                      onSelectSignature={(id) => ouvrirSignature(id)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        <div className="text-xs text-gray-400 mt-2">
          Une erreur « apparue pendant » une campagne n'a pas été causée par elle : c'est un repère
          temporel. Mesuré sur 36 h réelles, une erreur apparaît pendant une campagne 2 fois plus
          souvent que le hasard — un signal, pas une preuve.
        </div>
      </div>

      {selectedId && (
        <SignatureDetail
          signatureId={selectedId}
          // Le meme environnement que la liste d'ou la fiche a ete ouverte :
          // sans lui l'historique melangeait les quatre.
          env={activeEnv === 'all' ? null : activeEnv}
          probleme={selectedProbleme}
          filtres={{
            env: activeEnv === 'all' ? undefined : activeEnv,
            service: serviceFilter === 'all' ? undefined : serviceFilter,
            status: statusFilter === 'all' ? undefined : statusFilter,
            since: sinceDay,
            until: untilDay
          }}
          teams={teams}
          onClose={() => ouvrirSignature(null)}
          onUpdated={handleSignatureUpdated}
        />
      )}
    </div>
  );
}
